import { useEffect, useRef, useState } from 'react';

/**
 * Hook to detect when an element enters the viewport
 * @param {Object} options - IntersectionObserver options
 * @returns {Array} [ref, isVisible] - Reference to attach to element and visibility state
 */
export const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const threshold = options.threshold || 0.1;
    const rootMargin = options.rootMargin || '0px';
    const once = options.once !== false;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        // Once visible, stop observing (animation happens once)
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [options.threshold, options.rootMargin, options.once]);

  return [ref, isVisible];
};

/**
 * Stagger children animations with delay
 * @param {number} index - Index of the child element
 * @param {number} delay - Base delay in ms (default 100ms)
 * @returns {Object} - Style object with animation delay
 */
export const getStaggerDelay = (index, delay = 100) => ({
  animationDelay: `${index * delay}ms`,
  transitionDelay: `${index * delay}ms`
});
